import type {
  DocumentResult,
  JobDefinition,
  LearningAccelerationReport,
  LearningEligibility,
  LearningFieldMeasurement,
  LearningTableMeasurement,
} from '../shared/desktop-api';
import { countSkills, projectLearningSkills } from '../shared/learning-skills';
import { jobLearningAIQ } from '../renderer/job-learning-aiq';
import type { EngineCall } from './desktop-commands';

/** What a job looks like today for its learning note: skills Ready of all, as Learning counts them, and its AIQ. */
export type LearningReading = { skillsReady: number; skillsTotal: number; aiq: number | null };

/**
 * The numbers a weekly learning note is the difference of, read the way the Learning screen reads them, so a note and
 * Learning never disagree (CapturePoint's `readJobLearningNumbers`, main/email-asks-learning.ts): the skills Ready of
 * all from the same engine reads and the same projection (shared/learning-skills.ts), the AIQ as the header shows it.
 * Null while the engine is still counting its page designs (the type skills would drop out of the total) or when the
 * job is not the engine's: nothing is sent then, and the next day reads again.
 */
export async function readLearningReading(
  call: EngineCall,
  jobId: string,
): Promise<LearningReading | null> {
  const jobs = await call<JobDefinition[]>('list-jobs');
  const job = jobs.find((entry) => entry.id === jobId);
  if (!job) return null;
  const [eligibility, report, fieldResults, tableResults, batch] = await Promise.all([
    call<LearningEligibility>('learning-eligibility', { jobId }),
    call<LearningAccelerationReport>('learning-acceleration', { jobId }),
    call<LearningFieldMeasurement[]>('learning-field-results', { jobId }),
    call<LearningTableMeasurement[]>('learning-table-results', { jobId }),
    call<{ documents?: DocumentResult[] } | null>('latest-batch', { jobId }),
  ]);
  if (report?.layouts?.summaryPending === true) return null;
  const counts = countSkills(
    projectLearningSkills({
      job,
      eligibility,
      report,
      fieldResults,
      tableResults,
      documents: batch?.documents ?? null,
    }),
  );
  const aiq = report ? jobLearningAIQ(job, [], report)?.score : undefined;
  return {
    skillsReady: counts.ready,
    skillsTotal: counts.total,
    aiq: aiq === undefined ? null : Math.round(aiq),
  };
}

type Store = Pick<Storage, 'getItem' | 'setItem'>;

const sentKey = (jobId: string) => `paige.learningReading.${jobId}`;

/**
 * Once a UTC day per job and browser, while the person's learning note is on for the job: the reading goes to
 * `PUT {jobApi}/learning-reading`, which keeps it as the company's day. A failure is dropped; the next day reads again.
 */
export async function sendLearningReadingOnce(
  call: EngineCall,
  job: { workspaceJobId: string; jobApi: string; tenant?: string },
  store: Store | undefined = safeStorage(),
): Promise<boolean> {
  const today = new Date().toISOString().slice(0, 10);
  if (store?.getItem(sentKey(job.workspaceJobId)) === today) return false;
  try {
    const reading = await readLearningReading(call, job.workspaceJobId);
    if (!reading) return false;
    const response = await fetch(`${job.jobApi}/learning-reading`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(job.tenant ? { 'Paige-Tenant': job.tenant } : {}),
      },
      body: JSON.stringify(reading),
    });
    if (!response.ok) return false;
    store?.setItem(sentKey(job.workspaceJobId), today);
    return true;
  } catch {
    return false;
  }
}

function safeStorage(): Store | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}
